import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Button } from 'react-bootstrap';
import { GameHeaderBar } from './GameHeaderBar';

// Color shades data
const colorShades = [
  // Blue family
  { name: 'Navy', color: '#000080', family: 'blue' },
  { name: 'Sky Blue', color: '#87CEEB', family: 'blue' },
  { name: 'Turquoise', color: '#40E0D0', family: 'blue' },
  { name: 'Aqua', color: '#00FFFF', family: 'blue' },
  // Pink family
  { name: 'Rose', color: '#FF007F', family: 'pink' },
  { name: 'Light Pink', color: '#FFB6C1', family: 'pink' },
  { name: 'Hot Pink', color: '#FF69B4', family: 'pink' },
  { name: 'Magenta', color: '#FF00FF', family: 'pink' },
  // Green family
  { name: 'Lime', color: '#00FF00', family: 'green' },
  { name: 'Forest', color: '#228B22', family: 'green' },
  { name: 'Mint', color: '#98FF98', family: 'green' },
  { name: 'Olive', color: '#808000', family: 'green' }
];

const colorFamilies = ['blue', 'pink', 'green'];

export const ColorShadesGame = ({ handleClose, onGameComplete }) => {
  const [round, setRound] = useState(1);
  const [currentFamily, setCurrentFamily] = useState('blue');
  const [selectedShades, setSelectedShades] = useState([]);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);

  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const maxRounds = 3;

  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
  }, []);

  // Speak instruction when round or family changes
  useEffect(() => {
    if (!isMuted && !showFeedback) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(
          `Find all ${currentFamily} shades`
        );
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 600);

      return () => clearTimeout(speakTimeout);
    }
  }, [round, currentFamily, isMuted, volume, showFeedback]);

  const getFamilyEmoji = (family) => {
    const emojis = {
      blue: '💙',
      pink: '🩷',
      green: '💚'
    };
    return emojis[family];
  };

  const handleShadeClick = (shade) => {
    if (shade.family === currentFamily) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }
      
      const newSelectedShades = [...selectedShades, shade.name];
      setSelectedShades(newSelectedShades);
      
      const newScore = score + 10;
      setScore(newScore);
      
      const currentFamilyShades = colorShades.filter(s => s.family === currentFamily);
      const allSelected = newSelectedShades.filter(name => 
        currentFamilyShades.some(s => s.name === name)
      ).length === currentFamilyShades.length;
      
      if (allSelected) {
        setShowFeedback(true);
        setTimeout(() => {
          if (round === maxRounds) {
            const stars = 3;
            confetti({ particleCount: 250, spread: 100 });
            setTimeout(() => {
              if (onGameComplete) {
                onGameComplete(stars, newScore);
              }
              if (handleClose) {
                handleClose();
              }
            }, 2500);
          } else {
            const nextRound = round + 1;
            setRound(nextRound);
            setCurrentFamily(colorFamilies[nextRound - 1]);
            setSelectedShades([]);
            setShowFeedback(false);
          }
        }, 2000);
      }
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }
    }
  };

  const currentFamilyShades = colorShades.filter(s => s.family === currentFamily);
  const remainingInFamily = currentFamilyShades.filter(s => !selectedShades.includes(s.name)).length;

  return (
    <div className="tap-letter-board-bg">
      <GameHeaderBar
        scoreIcon="🎨"
        score={score}
        questionLabel="Find All:"
        question={<>{getFamilyEmoji(currentFamily)} {currentFamily.toUpperCase()}</>}
        volume={volume}
        onVolumeChange={(v) => {
          setVolume(v);
          localStorage.setItem('tapGameVolume', v);
        }}
        onReplay={() => {
          if (!isMuted) {
            const utterance = new window.SpeechSynthesisUtterance(`Find all ${currentFamily} shades`);
            utterance.rate = 0.8;
            utterance.volume = volume;
            window.speechSynthesis.speak(utterance);
          }
        }}
      />

      <div className="game-content-area">
        <div className="round-indicator">
          Round {round}/{maxRounds}
        </div>

        <div className="instruction-text" style={{ 
          color: '#fff', 
          fontSize: '1.3rem', 
          fontWeight: 'bold', 
          textAlign: 'center', 
          margin: '1rem 0',
          textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
        }}>
          {getFamilyEmoji(currentFamily)} Find {remainingInFamily} more {currentFamily} shade{remainingInFamily !== 1 ? 's' : ''}!
        </div>

        {!showFeedback ? (
          <div className="tap-letter-btn-grid">
            {colorShades.map(shade => {
              const isCorrectFamily = shade.family === currentFamily;
              const isSelected = selectedShades.includes(shade.name);
              
              return (
                <button
                  key={shade.name}
                  className={`btn m-1 letter-btn ${isSelected ? 'btn-success selected-correct' : 'btn-outline-primary'}`}
                  onClick={() => handleShadeClick(shade)}
                  disabled={isSelected}
                  style={{
                    background: isSelected ? '#000' : shade.color,
                    color: '#fff',
                    minWidth: '150px',
                    minHeight: '120px',
                    border: isSelected ? '3px solid rgb(0, 245, 253)' : 
                            isCorrectFamily ? '3px solid rgba(255,255,255,0.8)' :
                            '3px solid rgba(255,255,255,0.3)',
                    opacity: isSelected ? 0.7 : 1,
                    cursor: isSelected ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '1.25rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                    {shade.name}
                  </div>
                  {isSelected && (
                    <div style={{ fontSize: '1.5rem', marginTop: '4px' }}>✓</div>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="feedback-message">
            <h2 style={{ color: '#fff', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              🎉 Great Job! All {currentFamily} shades found! 🎉
            </h2>
          </div>
        )}
      </div>

      <style>{`
        .tap-letter-board-bg {
          background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
          background-size: cover, cover;
          background-position: center, center;
          border-radius: 32px;
          box-shadow: 0 6px 32px #e0c68a33;
          padding: 32px 12px 24px 12px;
          margin: 0 auto;
          max-width: 900px;
          width: 98%;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes board-pop {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .tap-header-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(224,198,138,0.10);
          padding: 1px 18px 18px 18px;
          margin-bottom: 18px;
          gap: 10px;
        }
        .tap-score-box,
        .tap-volume-box {
          display: flex;
          align-items: center;
          border-radius: 14px;
          padding: 6px 14px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #b8860b;
          background: rgba(255, 255, 255, 0.86);
          min-width: 110px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
        }
        .tap-score-label {
          margin-right: 6px;
          font-size: 1rem;
          color: #b8860b;
        }
        .tap-score-value {
          font-size: 1.1rem;
          color: #1976d2;
          margin-left: 4px;
          font-weight: 700;
        }
        .tap-letter-target {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(224,198,138,0.92);
          border-radius: 18px;
          padding: 8px 32px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          margin: 0 18px;
          min-width: 180px;
          flex: 1;
        }
        .tap-letter-label {
          font-size: 1.1rem;
          color: #3e2723;
          margin-right: 8px;
          font-weight: 600;
        }
        .tap-letter-value {
          font-size: 1.5rem;
          color: #1976d2;
          font-weight: 900;
          text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
        }
        .tap-volume-value {
          margin-left: 8px;
          font-size: 1rem;
          color: #1976d2;
          font-weight: 700;
          min-width: 36px;
          display: inline-block;
          text-align: right;
        }
        .game-content-area {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .round-indicator {
          background: rgba(255,255,255,0.9);
          padding: 8px 20px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 1.2rem;
          color: #1976d2;
          margin-bottom: 15px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .tap-letter-btn-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin-top: 10px;
          gap: 8px;
        }
        .letter-btn {
          padding: 8px;
          border-radius: 18px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.10);
          transition: transform 0.1s, box-shadow 0.2s;
        }
        .letter-btn:hover {
          transform: translateY(-5px) scale(1.05);
        }
        .selected-correct {
          border-color: rgb(0, 245, 253) !important;
          color: #fff !important;
          box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
        }
        .feedback-message {
          text-align: center;
          animation: bounceIn 0.5s ease;
          padding: 2rem;
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        @media (max-width: 700px) {
          .tap-header-bar {
            flex-direction: column;
            gap: 10px;
            padding-bottom: 10px;
          }
          .tap-letter-target {
            min-width: 0;
            width: 100%;
            padding: 8px 0;
            margin: 8px 0;
          }
          .letter-btn {
            min-width: 90px !important;
            min-height: 70px !important;
          }
        }
        @media (max-width: 480px) {
          .tap-letter-board-bg {
            padding: 10px;
          }
          .letter-btn {
            min-width: 80px !important;
            min-height: 60px !important;
          }
        }
      `}</style>
    </div>
  );
};
